/**
 * TourRequestModal Component - Modal for scheduling property tours
 *
 * Step 1: message + duration. Step 2: optional preferred time. The request is
 * created only on "Submit Request", so dismissing mid-flow never leaves a
 * half-finished request behind. Step 3 confirms the request was sent.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTourRequests, useTourAvailability } from '../../agents/tourRequestAgent';
import { toDateOnly } from '../OfferManagement/utils';
import Modal from '../ui/Modal';
import tourErrorMessage from './tourErrorMessage';
import './TourRequestModal.css';
import styles from './TourRequestModal.module.css';

/** One list for both steps, so a value picked on one step always exists on the other. */
const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
];

/** Format a naive "YYYY-MM-DDTHH:MM:SS" as local wall-clock text. */
const formatSlot = (naiveIso) =>
  new Date(naiveIso).toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Format only the time of a naive slot — no TZ math, HH:MM is wall-clock. */
const formatSlotTime = (naiveIso) =>
  new Date(`2000-01-01T${String(naiveIso).slice(11, 16)}:00`).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

const TourRequestModal = ({ buyerId, houseId, houseName, onClose, onSuccess }) => {
  const { createTourRequest } = useTourRequests(buyerId);
  const { availability, getAvailability } = useTourAvailability();

  const [step, setStep] = useState(1);
  const [message, setMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [duration, setDuration] = useState(30);
  // "date|duration" the current availability result was fetched for. A result
  // for any other date or length is stale and never shown or submitted.
  const [checkedFor, setCheckedFor] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sentSlot, setSentSlot] = useState(null);
  // Nothing is saved until Submit, so closing a filled-in form asks first.
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const keepEditingRef = useRef(null);

  const isDirty = message.trim() !== '' || selectedDate !== '' || duration !== 30;

  useEffect(() => {
    if (confirmDiscard) keepEditingRef.current?.focus();
  }, [confirmDiscard]);

  const availabilityKey = `${selectedDate}|${duration}`;
  // Only the chosen day's slots that are still ahead: the full ISO is what
  // gets submitted, so the date on the request always matches the slot the
  // seller actually offered, and a time earlier today is never offered.
  const now = new Date();
  const slotsForDate =
    availability && checkedFor === availabilityKey
      ? (availability.available_slots || []).filter(
          (slot) => String(slot).slice(0, 10) === selectedDate && new Date(slot) > now
        )
      : null;
  const chosenSlot = slotsForDate && slotsForDate.includes(selectedSlot) ? selectedSlot : null;

  const handleSubmitRequest = async () => {
    if (selectedDate && !chosenSlot) {
      setError('Pick one of the available times, or clear the date to let the seller propose one.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Message is optional. Default to a generic request when left blank.
      const finalMessage = message.trim()
        ? message
        : 'I would like an appointment to view your property.';
      // The tour stays 'pending' until the seller approves and confirms a
      // final time; the preferred slot is only a suggestion.
      const naiveIso = chosenSlot
        ? String(chosenSlot).replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '')
        : null;
      await createTourRequest(
        houseId,
        finalMessage,
        naiveIso ? [naiveIso] : [],
        duration
      );
      setSentSlot(naiveIso);
      setStep(3);
    } catch (err) {
      setError(tourErrorMessage(err, 'Error creating tour request'));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAvailability = async () => {
    const key = availabilityKey;
    setChecking(true);
    setError(null);
    const startDate = new Date(selectedDate);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);
    // getAvailability reports failure by resolving undefined, not throwing.
    const data = await getAvailability(houseId, startDate, endDate, duration);
    setChecking(false);
    if (data) {
      setCheckedFor(key);
    } else {
      setError('Error checking availability. Please try again.');
    }
  };

  // Once the request is sent, any dismissal is a successful finish. Before
  // that, Escape / × on a filled-in form asks first; a second press discards.
  const handleClose = () => {
    if (loading) return;
    if (step === 3) {
      (onSuccess || onClose)?.();
    } else if (isDirty && !confirmDiscard) {
      setConfirmDiscard(true);
    } else {
      onClose?.();
    }
  };

  const durationSelect = (id, label) => (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        value={duration}
        onChange={(e) => setDuration(parseInt(e.target.value, 10))}
      >
        {DURATION_OPTIONS.map(({ value, label: text }) => (
          <option key={value} value={value}>{text}</option>
        ))}
      </select>
    </div>
  );

  const footer = confirmDiscard ? (
    <>
      <span className={styles.discardPrompt}>Discard this tour request?</span>
      <button
        ref={keepEditingRef}
        className="btn-secondary"
        onClick={() => setConfirmDiscard(false)}
      >
        Keep editing
      </button>
      <button className="btn-primary" onClick={() => onClose?.()}>
        Discard
      </button>
    </>
  ) : step === 3 ? (
      <button className="btn-primary" onClick={handleClose}>
        Done
      </button>
    ) : (
      <>
        {step === 2 && (
          <button
            className="btn-secondary"
            onClick={() => {
              setError(null);
              setStep(1);
            }}
            disabled={loading}
          >
            Back
          </button>
        )}
        <button
          className="btn-primary"
          onClick={step === 1 ? () => setStep(2) : handleSubmitRequest}
          disabled={loading}
        >
          {loading ? 'Sending…' : step === 1 ? 'Continue' : 'Submit Request'}
        </button>
      </>
    );

  return (
    <Modal
      open
      onClose={handleClose}
      title={step === 3 ? 'Tour request sent' : 'Request a Tour'}
      footer={footer}
      // Nothing is saved until Submit, so a stray backdrop click must not
      // throw away what the buyer typed. Escape and × still close.
      dismissOnOverlayClick={false}
    >
      <div className="tour-request-modal">
        {step === 1 && (
          <>
            <div className="step-header">
              <h3>Tell Us About Your Interest</h3>
              <p>{houseName}</p>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              <div className="form-group">
                <label htmlFor="tour-message">Message (optional — why are you interested?)</label>
                <textarea
                  id="tour-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g., Great location, love the kitchen, within budget..."
                  rows="4"
                />
              </div>

              {durationSelect('tour-duration', 'Tour Duration')}
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <div className="step-header">
              <h3>Suggest a Preferred Time</h3>
              <p>
                Optional. Your request will be sent to the seller for approval.
                They will confirm a final time after reviewing.
              </p>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              <div className="form-group">
                <label htmlFor="tour-preferred-date">Preferred Date</label>
                <input
                  id="tour-preferred-date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedSlot(null);
                  }}
                  min={toDateOnly(new Date())}
                />
              </div>

              {selectedDate && (
                <>
                  {durationSelect('tour-duration-confirm', 'Duration (minutes)')}

                  <button
                    type="button"
                    className="btn-check"
                    onClick={handleCheckAvailability}
                    disabled={checking}
                  >
                    {checking ? 'Checking…' : 'Check Availability'}
                  </button>

                  {slotsForDate && slotsForDate.length === 0 && (
                    <p className={styles.noSlots}>
                      No open times on this date. Try another date, or clear the
                      date and submit, and the seller will propose a time.
                    </p>
                  )}

                  {slotsForDate && slotsForDate.length > 0 && (
                    <div className="form-group">
                      <label>Available Times</label>
                      <div className="time-slots">
                        {slotsForDate.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            className={`time-slot ${chosenSlot === slot ? 'selected' : ''}`}
                            aria-pressed={chosenSlot === slot}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            {formatSlotTime(slot)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </form>
          </>
        )}

        {step === 3 && (
          <div className={styles.sent} role="status">
            <div className={styles.sentIcon} aria-hidden="true">✓</div>
            <p>
              Your tour request for <strong>{houseName}</strong> is on its way
              to the seller.
            </p>
            <p>
              {sentSlot
                ? `You suggested ${formatSlot(sentSlot)} (${duration} min). `
                : `You didn't suggest a time, so the seller will propose one (${duration} min). `}
              You'll be notified when they respond. Track it anytime under{' '}
              <strong>Tour Requests</strong>.
            </p>
          </div>
        )}

        {error && step !== 3 && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TourRequestModal;
